import { useRef, useState } from 'react'
import {
  Download,
  Eye,
  FileText,
  ImageIcon,
  Trash2,
  Upload,
} from 'lucide-react'
import { SopViewer } from '@/components/files/SopViewer'
import { Button } from '@/components/ui/Button'
import {
  downloadDataUrl,
  formatFileSize,
  isImageName,
  isPdfName,
  readFileAsDataUrl,
  uniqueName,
} from '@/lib/files'
import type { CatalogSopFile } from '@/services/catalog'

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.webp'

type SopWorkspaceProps = {
  files: CatalogSopFile[]
  onChange: (files: CatalogSopFile[]) => void
  /** Catalog edit: true. Read-only list can pass false. */
  editable?: boolean
  allowDownload?: boolean
  compact?: boolean
}

function newSopId(): string {
  return crypto.randomUUID()
}

export function SopWorkspace({
  files,
  onChange,
  editable = true,
  allowDownload = true,
  compact = false,
}: SopWorkspaceProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [viewingId, setViewingId] = useState<string | null>(null)
  const viewing = viewingId
    ? files.find((f) => f.id === viewingId) ?? files[0]
    : null

  async function onPick(list: FileList | null) {
    if (!list?.length || !editable) return
    setError(null)
    try {
      const existingNames = files.map((f) => f.name)
      const next: CatalogSopFile[] = []
      for (const file of Array.from(list)) {
        if (!isImageName(file.name) && !isPdfName(file.name)) {
          setError('Chỉ nhận ảnh (PNG/JPG/…) hoặc PDF.')
          continue
        }
        const name = uniqueName(
          [...existingNames, ...next.map((f) => f.name)],
          file.name,
        )
        const dataUrl = await readFileAsDataUrl(file)
        next.push({
          id: newSopId(),
          name,
          dataUrl,
          uploadedAt: new Date().toISOString(),
          size: file.size,
        })
      }
      if (next.length) onChange([...files, ...next])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không tải được file')
    } finally {
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  function remove(id: string) {
    onChange(files.filter((f) => f.id !== id))
    if (viewingId === id) setViewingId(null)
  }

  return (
    <div
      className={`rounded-[var(--radius-sm)] border border-slate-200 bg-slate-50/80 ${
        compact ? 'p-2' : 'p-2.5'
      }`}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <div className="text-xs font-semibold tracking-wide text-slate-600 uppercase">
          SOP workspace
          {files.length ? (
            <span className="ml-1 font-normal text-slate-400 normal-case">
              ({files.length} file)
            </span>
          ) : null}
        </div>
        {editable ? (
          <>
            <input
              ref={inputRef}
              type="file"
              className="hidden"
              accept={ACCEPT}
              multiple
              onChange={(e) => void onPick(e.target.files)}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => inputRef.current?.click()}
            >
              <Upload className="h-3.5 w-3.5" />
              Tải lên
            </Button>
          </>
        ) : null}
      </div>

      {error ? (
        <p className="mb-1.5 text-xs text-red-600">{error}</p>
      ) : null}

      {files.length === 0 ? (
        <p className="py-3 text-center text-xs text-slate-400">
          Chưa có file SOP. Tải ảnh hoặc PDF (nhiều file).
        </p>
      ) : (
        <ul className="max-h-48 space-y-1 overflow-y-auto">
          {files.map((file) => {
            const Icon = isImageName(file.name) ? ImageIcon : FileText
            return (
              <li
                key={file.id}
                className="flex items-center gap-1.5 rounded border border-slate-200 bg-white px-2 py-1.5 text-xs"
              >
                <Icon className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                <button
                  type="button"
                  className="min-w-0 flex-1 truncate text-left font-medium text-[var(--color-link)] hover:underline"
                  title={file.name}
                  onClick={() => setViewingId(file.id)}
                >
                  {file.name}
                </button>
                {file.size != null ? (
                  <span className="shrink-0 text-xs text-slate-400">
                    {formatFileSize(file.size)}
                  </span>
                ) : null}
                <button
                  type="button"
                  className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                  title="Xem"
                  onClick={() => setViewingId(file.id)}
                >
                  <Eye className="h-3.5 w-3.5" />
                </button>
                {allowDownload ? (
                  <button
                    type="button"
                    className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                    title="Tải xuống"
                    onClick={() => void downloadDataUrl(file.name, file.dataUrl)}
                  >
                    <Download className="h-3.5 w-3.5" />
                  </button>
                ) : null}
                {editable ? (
                  <button
                    type="button"
                    className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
                    title="Gỡ file"
                    onClick={() => remove(file.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}

      {viewing ? (
        <SopViewer
          files={files}
          activeId={viewing.id}
          onClose={() => setViewingId(null)}
          onSelect={setViewingId}
          allowDownload={allowDownload}
        />
      ) : null}
    </div>
  )
}
